import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  PlayCircle,
  BookOpen,
  MessageCircle,
  Bell,
  Play,
  ChevronRight,
  CheckCircle2,
  Beaker,
  AlertCircle,
  WifiOff,
  Loader2,
  LogOut,
  MapPin,
  ExternalLink,
  Trophy,
  FlaskConical,
  Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getDistanceFromLatLonInMeters } from "@/utils/location";
import { MicroQuiz } from "@/components/MicroQuiz";
import { useToast } from "@/hooks/use-toast";

// Navigation
const navItems = [
  { id: "home", icon: Home, label: "Home" },
  { id: "classes", icon: PlayCircle, label: "Classes" },
  { id: "cards", icon: BookOpen, label: "Cards" },
  { id: "doubts", icon: MessageCircle, label: "Ask" },
];

// Mock data
const currentClass = {
  title: "Chemical Bonding",
  subtitle: "Ionic vs Covalent Bonds",
  progress: 65,
  duration: "45:20",
  currentTime: "29:28",
  lesson: 8,
  totalLessons: 12
};

const todaySchedule = [
  { time: "10:00 AM", title: "Organic Chemistry", status: "live", subject: "Hydrocarbons" },
  { time: "2:00 PM", title: "Physical Chemistry", status: "upcoming", subject: "Thermodynamics" },
  { time: "4:30 PM", title: "Doubt Session", status: "upcoming", subject: "AI Assisted" },
];

const chapters = [
  { name: "Chemical Bonding", completed: 8, total: 12 },
  { name: "Organic Reactions", completed: 6, total: 15 },
  { name: "Solutions", completed: 7, total: 8 },
];

const videoLibrary = [
  { id: "1", classLevel: "11", chapter: "Structure of Atom", title: "Bohr Model Explained", duration: "15 min", url: "https://www.youtube.com/results?search_query=Bohr+Model" },
  { id: "2", classLevel: "11", chapter: "Structure of Atom", title: "Quantum Numbers", duration: "12 min", url: "https://www.youtube.com/results?search_query=Quantum+Numbers+Chemistry" },
  { id: "3", classLevel: "11", chapter: "Chemical Bonding", title: "Ionic vs Covalent Bonds", duration: "18 min", url: "https://www.youtube.com/results?search_query=Ionic+Covalent+Bonds" },
  { id: "4", classLevel: "11", chapter: "Chemical Bonding", title: "VSEPR Theory", duration: "14 min", url: "https://www.youtube.com/results?search_query=VSEPR+Theory" },
  { id: "5", classLevel: "12", chapter: "Solutions", title: "Raoult's Law", duration: "16 min", url: "https://www.youtube.com/results?search_query=Raoult+Law+Chemistry" },
  { id: "6", classLevel: "12", chapter: "Electrochemistry", title: "Galvanic Cells", duration: "20 min", url: "https://www.youtube.com/results?search_query=Galvanic+Cells" },
  { id: "7", classLevel: "12", chapter: "Chemical Kinetics", title: "Rate of Reaction", duration: "17 min", url: "https://www.youtube.com/results?search_query=Rate+of+Reaction" },
];

// Smart Attendance Widget
const SmartAttendance = () => {
  const [status, setStatus] = useState<'idle' | 'checking' | 'success' | 'far'>('idle');
  const [distance, setDistance] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkIn = () => {
    setStatus('checking');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const dist = getDistanceFromLatLonInMeters(
          pos.coords.latitude, pos.coords.longitude,
          28.6139, 77.2090
        );
        setDistance(Math.round(dist));
        setStatus(dist <= 100 ? 'success' : 'far');
      },
      () => setStatus('idle'),
      { enableHighAccuracy: true }
    );
  };

  if (status === 'success') {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900">Checked In ✓</p>
              <p className="text-sm text-slate-600">{distance}m from class</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setStatus('idle')}>Reset</Button>
        </CardContent>
      </Card>
    );
  }

  if (status === 'far') {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-bold text-slate-900">Too Far Away</p>
              <p className="text-sm text-slate-600">{distance}m away (need ≤100m)</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setStatus('idle')}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-slate-500" />
            <div>
              <p className="font-bold text-slate-900">Smart Attendance</p>
              <p className="text-sm text-slate-600">Mark present when in class</p>
            </div>
          </div>
          <Button
            onClick={checkIn}
            disabled={status === 'checking'}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {status === 'checking' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking...</>
            ) : (
              "Mark Present"
            )}
          </Button>
        </div>
        {!isOnline && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <WifiOff className="h-3 w-3" />
            Offline mode - will sync when online
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Classes Tab View
const ClassesView = () => {
  const [selectedClass, setSelectedClass] = useState<'11' | '12'>('11');
  const filtered = videoLibrary.filter(v => v.classLevel === selectedClass);

  const grouped = filtered.reduce((acc, video) => {
    if (!acc[video.chapter]) acc[video.chapter] = [];
    acc[video.chapter].push(video);
    return acc;
  }, {} as Record<string, typeof videoLibrary>);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Video Classes</h1>
        <p className="text-slate-600 mt-1">Watch recorded lessons by chapter</p>
      </div>

      {/* Class Toggle */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
        <button
          onClick={() => setSelectedClass('11')}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${selectedClass === '11' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}
        >
          Class 11
        </button>
        <button
          onClick={() => setSelectedClass('12')}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${selectedClass === '12' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}
        >
          Class 12
        </button>
      </div>

      {/* Video List */}
      <div className="space-y-4">
        {Object.entries(grouped).map(([chapter, videos]) => (
          <Card key={chapter} className="border-slate-200">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-blue-500" />
                {chapter}
                <Badge className="ml-auto bg-blue-100 text-blue-700 text-xs font-medium px-2">{videos.length} videos</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {videos.map(video => (
                <a
                  key={video.id}
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all group"
                >
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-red-200 transition-colors">
                    <Play className="h-4 w-4 text-red-600" fill="currentColor"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{video.title}</p>
                    <p className="text-xs text-slate-500">{video.duration}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-blue-500 shrink-0" />
                </a>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Cards (Progress) Tab View
const CardsView = ({ onOpenQuiz }: { onOpenQuiz: (topic: string) => void }) => (
  <div className="space-y-5">
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Study Cards</h1>
      <p className="text-slate-600 mt-1">Track your chapter progress and practice</p>
    </div>

    <div className="space-y-4">
      {chapters.map((chapter, i) => {
        const pct = Math.round((chapter.completed / chapter.total) * 100);
        return (
          <Card key={i} className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-slate-900">{chapter.name}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{chapter.completed} of {chapter.total} lessons done</p>
                </div>
                <span className={`text-lg font-bold ${pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {pct}%
                </span>
              </div>

              {/* Lesson Beakers */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {Array.from({ length: chapter.total }).map((_, idx) => (
                  <button
                    key={idx}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all text-xs font-bold ${
                      idx < chapter.completed
                        ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        : idx === chapter.completed
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300 ring-offset-1 hover:bg-blue-700'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-60'
                    }`}
                    title={
                      idx < chapter.completed
                        ? `Lesson ${idx + 1} — Completed`
                        : idx === chapter.completed
                        ? `Lesson ${idx + 1} — Up Next`
                        : `Lesson ${idx + 1} — Locked`
                    }
                  >
                    {idx < chapter.completed ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Beaker className="h-4 w-4" />
                    )}
                  </button>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>

              <Button
                onClick={() => onOpenQuiz(chapter.name)}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                size="sm"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Boost Score — Take a Quiz
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  </div>
);

// Home Tab View
const HomeView = ({ onSwitchTab, toast }: { onSwitchTab: (tab: string) => void; toast: ReturnType<typeof useToast>['toast'] }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Welcome back, Student!</h1>
      <p className="text-slate-600 mt-1">Here's what's happening today</p>
    </div>

    {/* Today's Schedule */}
    <section>
      <h2 className="text-lg font-bold text-slate-900 mb-3">Today's Schedule</h2>
      <div className="space-y-3">
        {/* LIVE Class */}
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-red-600 uppercase">Live Now</span>
                  <span className="text-xs text-slate-600">10:00 AM</span>
                </div>
                <h3 className="font-bold text-slate-900">Organic Chemistry</h3>
                <p className="text-sm text-slate-600">Hydrocarbons</p>
              </div>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => {
                  toast({ title: "Joining Live Class…", description: "Connecting to Organic Chemistry session" });
                  onSwitchTab("classes");
                }}
              >
                Join Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {todaySchedule.slice(1).map((item, i) => (
          <Card key={i} className="border-slate-200 bg-slate-50 opacity-70">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{item.time} • Upcoming</p>
                  <h3 className="font-medium text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.subject}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>

    {/* Stats */}
    <section>
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-slate-900">5</div>
            <div className="text-sm font-medium text-slate-600 mt-1">Day Streak</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-slate-900">78%</div>
            <div className="text-sm font-medium text-slate-600 mt-1">Avg Score</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-slate-900">12h</div>
            <div className="text-sm font-medium text-slate-600 mt-1">Study Time</div>
          </CardContent>
        </Card>
      </div>
    </section>

    {/* Continue Learning */}
    <section>
      <h2 className="text-lg font-bold text-slate-900 mb-3">Continue Learning</h2>
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <Play className="h-6 w-6 text-white" fill="white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-slate-900">{currentClass.title}</h3>
              <p className="text-slate-600">{currentClass.subtitle}</p>

              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Lesson {currentClass.lesson} of {currentClass.totalLessons}</span>
                  <span className="text-sm font-bold text-blue-600">{currentClass.progress}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{ width: `${currentClass.progress}%` }} />
                </div>
              </div>

              <div className="mt-4 flex gap-3 flex-wrap">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    toast({ title: "Resuming Lesson", description: `Chemical Bonding — resuming at ${currentClass.currentTime}` });
                    onSwitchTab("classes");
                  }}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Resume at {currentClass.currentTime}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onSwitchTab("classes")}
                >
                  View All Lessons
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>

    {/* Progress */}
    <section>
      <h2 className="text-lg font-bold text-slate-900 mb-3">Your Progress</h2>
      <div className="space-y-3">
        {chapters.map((chapter, i) => (
          <Card key={i} className="border-slate-200 hover:border-blue-300 transition-colors cursor-pointer" onClick={() => onSwitchTab("cards")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900">{chapter.name}</h3>
                <span className="text-sm text-slate-600">{chapter.completed}/{chapter.total}</span>
              </div>

              <div className="flex flex-wrap gap-1">
                {Array.from({ length: chapter.total }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSwitchTab("cards");
                    }}
                    className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                      idx < chapter.completed
                        ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        : idx === chapter.completed
                        ? 'bg-blue-600 text-white hover:bg-blue-700 ring-2 ring-blue-300'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                    title={idx < chapter.completed ? `Lesson ${idx + 1} (completed)` : idx === chapter.completed ? `Lesson ${idx + 1} (next)` : `Lesson ${idx + 1} (locked)`}
                  >
                    <Beaker className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>

              {chapter.completed < chapter.total && (
                <p className="text-xs text-blue-600 mt-2">Click to continue →</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>

    {/* Smart Attendance */}
    <section>
      <h2 className="text-lg font-bold text-slate-900 mb-3">Attendance</h2>
      <SmartAttendance />
    </section>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("home");
  const [quizTopic, setQuizTopic] = useState<string | null>(null);

  const handleNavClick = (id: string) => {
    if (id === "doubts") {
      navigate("/ask-doubt");
    } else {
      setActiveTab(id);
    }
  };

  const openQuiz = (topic: string) => setQuizTopic(topic);
  const closeQuiz = () => setQuizTopic(null);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className="font-bold text-lg text-slate-900">ChemSmart</span>
          </div>

          {/* Desktop tab nav */}
          <nav className="hidden md:flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = item.id !== "doubts" ? activeTab === item.id : false;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-slate-100 relative">
              <Bell className="h-5 w-5 text-slate-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <button
              onClick={() => navigate("/")}
              className="p-2 rounded-lg hover:bg-slate-100"
              title="Logout"
            >
              <LogOut className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 pb-24 md:pb-8">
        {activeTab === "home" && <HomeView onSwitchTab={setActiveTab} toast={toast} />}
        {activeTab === "classes" && <ClassesView />}
        {activeTab === "cards" && <CardsView onOpenQuiz={openQuiz} />}
      </main>

      {/* MicroQuiz Dialog */}
      {quizTopic && (
        <MicroQuiz topic={quizTopic} open={!!quizTopic} onClose={closeQuiz} />
      )}

      {/* Bottom Navigation - Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.id !== "doubts" ? activeTab === item.id : false;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`flex flex-col items-center gap-0.5 px-4 py-2 rounded-lg transition-colors ${
                  isActive ? "text-blue-600 bg-blue-50" : "text-slate-500"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
